import React, { useState, useEffect } from "react";
import { useMediaQuery } from "@chakra-ui/react";
import {
  Box,
  Button, 
  Flex,
  Heading,
  Image,
  LinkBox,
  LinkOverlay,
  Text
} from "@chakra-ui/react";
import {
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  TwitterIcon,
  WhatsappShareButton,
  WhatsappIcon,
  LinkedinShareButton,
  LinkedinIcon,
  WeiboShareButton,
  WeiboIcon,
  EmailShareButton,
  EmailIcon,
} from "react-share";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, A11y } from "swiper";
import enableSwiperClicksFromNav from "../../helpers/enableSwiperClicksFromNav";
import { handleAppendArrowsToSliders } from "../../helpers/handleAppendArrowsToSliders";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/a11y";
import "./sliderLandscapeFestival.scss";
import { SliderProps } from "../../types/homePageTypes";


// slider component with square-shaped slides for year round home page

const SliderLandscapeFestival = ({ 
  title,
  description,
  isWebProgrammeSlider,
  sliderItems
}: SliderProps) => {
  const [isLargerThan1536] = useMediaQuery("(min-width: 1536px)");
  const [isLargerThan1200] = useMediaQuery("(min-width: 1200px)");
  const [isLargerThan992] = useMediaQuery("(min-width: 992px)");
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const [isLargerThan576] = useMediaQuery("(min-width: 567px)");
  const [isSmallerThan576] = useMediaQuery("(max-width: 566px)");
  const { swiperRef } = enableSwiperClicksFromNav();

  let slidesPerView;
  if (isSmallerThan576) {
    slidesPerView = 1.2;
  } else if (isLargerThan576 && !isLargerThan768) {
    slidesPerView = 1.3;
  } else if (isLargerThan768 && !isLargerThan992) {
    slidesPerView = 1.4
  } else if (isLargerThan992 && !isLargerThan1200) {
    slidesPerView = 1.7
  } else if (isLargerThan1200 && !isLargerThan1536) {
    slidesPerView = 2.28
  } else {
    slidesPerView = 2.2
  }

  const [shareIconClick, setShareIconClick] = useState<boolean>(false);
  const [currInd, setCurrInd] = useState<number | null | undefined>(null);
  const handleShareClick: (e:React.MouseEvent<HTMLButtonElement, MouseEvent> | React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLButtonElement> | React.FocusEvent<HTMLButtonElement, Element>, ind?: number) => void = (e, ind) => {
    e.preventDefault();
    setShareIconClick(!shareIconClick);
    setCurrInd(ind);
  }

  // create an id for the swiper component
  let swiperId: string = title ? `cubes-${title.replace(/[^a-zA-Z0-9]/g, '')}` : "cubes-0";
  if (swiperId.length > 25) {
    swiperId = swiperId.substring(0, 25);
  }

  // add slider next and previous arrows to target areas
  const nextTarget = `#${ swiperId }.sliderLandscapeFestival .swiper-button-next`;
  const prevTarget = `#${ swiperId }.sliderLandscapeFestival .swiper-button-prev`;
  handleAppendArrowsToSliders(nextTarget, prevTarget);

  return (
    <Box
      marginBottom={{ base: "50px !important", sm: "80px !important" }}
      marginTop="0 !important"
    >
      {/* section title */}
      <Heading
        as="h2"
        size={{ base: "section-header-mobile", sm: "section-header-tablet", lg: "section-header-desktop" }} 
        marginBottom={{ base: "12px !important", sm: "20px !important" }}
        marginLeft={{ base: "24px !important", sm: "28px !important" }}
      >
        { title }
      </Heading>

      {/* section description */}
      <Text
        size={{ base: "bravo-mobile", sm: "bravo", lg: "bravo-desktop" }}
        color="black !important"
        marginTop="0"
        marginBottom={{ base: "12px !important", sm: "20px !important" }}
        marginLeft={{ base: "24px !important", sm: "28px !important" }}
        p="0 30px 0 0"
      >
        { description }
      </Text>

      {/* mobile view */}
      {!isLargerThan1200 &&
        <>
          <Swiper
            id={ swiperId }
            className="sliderLandscapeFestival"
            modules={[ A11y ]}
            slidesPerView={ slidesPerView }
            spaceBetween={ 10 }
          >
            {sliderItems.map(( slide, index ) => {
              return (
                <SwiperSlide 
                  key={ index }
                  className={ currInd === index ? "swiper-active-slide" : "swiper-inactive-slide"}
                >
                  <LinkBox>
                    <LinkOverlay href={ slide.slug } aria-label={ slide.buttonText }>
                      <Image
                        w="100%"
                        objectFit="cover"
                        src={ slide.posterUrl }
                        alt={ slide.posterAlt }
                      />
                    </LinkOverlay>
                  </LinkBox>
                  <Flex
                    w="100%"
                    p={{ base: "24px 16px 8px 16px", sm: "30px 38px 0px 38px" }}
                    direction="column"
                    justifyContent="flex-start !important"
                  >
                    {/* label */}
                    {slide.webProgramme.length > 0 &&
                      <Flex 
                        marginBottom={{ base: "16px" }}
                        direction="row"
                        flexWrap="wrap"
                      >
                        {slide.webProgramme.map(( label, labelIndex, arr ) => {
                          return (
                            <Text
                              key={ labelIndex }
                              w="fit-content"
                              size={{ base: "program-tag-mobile", sm: "program-tag-tablet" }}
                              fontWeight="600 !important"
                              lineHeight="18px"
                              color="black !important"
                              borderLeft={`4px solid ${label.colour ? label.colour : "#000"}`}
                              borderBottom="none !important"
                              padding="0px 6px !important"
                            >
                              { labelIndex === arr.length - 1 ? label.title : `${label.title},` } 
                            </Text>
                          )})
                        }
                      </Flex>
                    }

                    {/* title */}
                    {slide.title &&
                      <Box marginBottom={{ base: "16px" }}>
                        <Heading
                          as="h3"
                          size={{ base: "portrait-slider-header-mobile", sm: "portrait-slider-header-tablet" }}
                          textAlign="left"
                        >
                          { slide.title }
                        </Heading>
                      </Box>
                    }

                    {(slide.isWebProgrammeSlide || slide.isFestivalFilm) &&
                      <>
                        {/* icons */}
                        <Flex
                          direction="row"
                          gap={{ base: "18px", sm: "21px", xl: "25px" }}
                          justifySelf="flex-end"
                          alignItems="flex-start"
                        >
                          {/* TIFFR */}
                          <Button
                            className="tiffr-shortlist" 
                            data-show-id={slide.id} 
                            style={{
                              minWidth: "unset",
                              margin: 0,
                              padding: 0,
                              border: "none",
                              background: "none",
                            }}
                            _hover={{ 
                              bgColor: "unset !important" 
                            }}
                          >
                            <span className="sr-only">Tiffr button</span>
                          </Button>

                          {/* share icon */}                       
                          <a className={"shareLink"} href="#" onClick={(e) => handleShareClick(e, index)}>
                            <Image 
                              className={"shareSvgImg"} 
                              h="23px" 
                              w="21px"
                              src="/img/Share.svg" 
                              alt="Share icon" 
                            />
                          </a>

                          {/* share window */}
                          {shareIconClick && index === currInd &&
                            <div className={"shareContainer"}>
                                <div className={"shareInnerContainer"}>
                                    <div className={"shareContainerArrow"}></div>
                                    <FacebookShareButton url={`https://tiff.net/${slide.slug}`}>
                                        <FacebookIcon size={50} />
                                    </FacebookShareButton>
                                    <TwitterShareButton url={`https://tiff.net/${slide.slug}`}>
                                        <TwitterIcon size={50} />
                                    </TwitterShareButton>
                                    <WhatsappShareButton url={`https://tiff.net/${slide.slug}`}>
                                        <WhatsappIcon size={50} />
                                    </WhatsappShareButton>
                                    <LinkedinShareButton url={`https://tiff.net/${slide.slug}`}>
                                        <LinkedinIcon size={50} />
                                    </LinkedinShareButton>
                                    <WeiboShareButton url={`https://tiff.net/${slide.slug}`}>
                                        <WeiboIcon size={50} />
                                    </WeiboShareButton>
                                    <EmailShareButton url={`https://tiff.net/${slide.slug}`} >
                                        <EmailIcon size={50} />
                                    </EmailShareButton>

                                    <button className={"shareClose"} onClick={(e) => handleShareClick(e)} onKeyDown={(e) => handleShareClick(e)} onBlur={(e) => handleShareClick(e)}>
                                        <i className='material-icons'>close</i>
                                    </button>
                                </div>
                            </div>
                          }
                        </Flex>
                      </>
                    }
                  </Flex>
                </SwiperSlide>
              )
            })}
          </Swiper>
        </>
      }

      {/* desktop view */}
      {isLargerThan1200 &&
        <Swiper
          id={ swiperId }
          className="sliderLandscapeFestival"
          onSwiper={( swiperInstance ) => { swiperRef.current = swiperInstance }}
          modules={[ Navigation, A11y ]}
          navigation={ true }
          slidesPerView={ slidesPerView }
          spaceBetween={ 10 }
        >
          {sliderItems.map(( slide, index ) => {
            return (
              <SwiperSlide
                key={ index }
                className={ currInd === index ? "swiper-active-slide" : "swiper-inactive-slide"}
              >
                <Image
                  w="100%"
                  src={ slide.posterUrl }
                  alt={ slide.posterAlt }
                  objectFit="cover"
                  _hover={{
                    filter: "blur(8px)",
                  }}
                  transition="0.3s ease-in-out"
                />
                <LinkBox
                  display="flex"
                  position="absolute"
                  top="0"
                  left="0"
                  h="100%"
                  w="100%"
                  bg="rgba(0, 0, 0, 0.3)"
                  opacity="0"
                  _hover={{
                    opacity: "1",
                    transform: "translateX(0)"
                  }}
                  _focusWithin={{
                    opacity: "1",
                    transform: "translateX(0)"
                  }}
                  transition="0.3s ease-in-out, transform 0.3s ease-out"
                  p={{ md: "46px" }}
                  flexDirection="column"
                  justifyContent="center !important"
                  backdropFilter="blur(2px)"
                  transform="translateX(-5px)"
                >
                  <LinkOverlay href={ slide.slug } aria-label={slide.buttonText}>
                    {/* label */}
                    {slide.webProgramme.length > 0 &&
                      <Flex 
                        marginBottom="16px"
                        direction="row"
                        gap="10px"
                        flexWrap="wrap"
                      >
                        {slide.webProgramme.map(( label, labelIndex ) => {
                            return (
                              <Text
                                key={ labelIndex }
                                w="fit-content"
                                size={{ base: "program-tag-desktop" }}
                              >
                                { label.title }
                              </Text>
                          )})
                        }
                      </Flex>
                    }

                    {/* title */}
                    {slide.title &&
                      <Box marginBottom="23px">
                        <Heading
                          as="h3"
                          size={{ md: "portrait-slider-header-desktop" }}
                          textAlign="left"
                        >
                          { slide.title }
                        </Heading>
                      </Box>
                    }

                    {(slide.isWebProgrammeSlide || slide.isFestivalFilm) &&
                      <>
                        {/* icons */}
                        <Flex
                          direction="row"
                          gap="18px"
                          justifySelf="flex-end"
                          alignItems="center"
                          position="absolute"
                          bottom="28px"
                        >
                          {/* TIFFR */}
                          <Button
                            className="tiffr-shortlist" 
                            data-show-id={slide.id} 
                            style={{
                              minWidth: "unset",
                              margin: 0,
                              padding: 0,
                              border: "none",
                              background: "none",
                            }}
                            _hover={{ 
                              bgColor: "unset !important" 
                            }}
                          >
                            <span className="sr-only">Tiffr button</span>
                          </Button>

                          {/* share icon */}                       
                          <Button 
                            className={"shareLink"} 
                            onClick={(e) => handleShareClick(e, index)}
                            background="transparent"
                            m="0"
                            p="0"
                            border="none"
                            _hover={{
                              background: "none !important",
                              border: "none !important",
                            }}
                            _focus={{
                              border: "1px solid white !important",
                            }}
                          >
                            <Image 
                              className={"shareSvgImg"} 
                              h="21px" 
                              w="21px" 
                              src="/img/Share.svg" 
                              alt="Share icon" 
                            />
                          </Button>

                          {/* share window */}
                          {shareIconClick && index === currInd &&
                            <div className={"shareContainer"}>
                                <div className={"shareInnerContainer"}>
                                    <div className={"shareContainerArrow"}></div>
                                    <FacebookShareButton url={`https://tiff.net/${slide.slug}`}>
                                        <FacebookIcon size={50} />
                                    </FacebookShareButton>
                                    <TwitterShareButton url={`https://tiff.net/${slide.slug}`}>
                                        <TwitterIcon size={50} />
                                    </TwitterShareButton>
                                    <WhatsappShareButton url={`https://tiff.net/${slide.slug}`}>
                                        <WhatsappIcon size={50} />
                                    </WhatsappShareButton>
                                    <LinkedinShareButton url={`https://tiff.net/${slide.slug}`}>
                                        <LinkedinIcon size={50} />
                                    </LinkedinShareButton>
                                    <WeiboShareButton url={`https://tiff.net/${slide.slug}`}>
                                        <WeiboIcon size={50} />
                                    </WeiboShareButton>
                                    <EmailShareButton url={`https://tiff.net/${slide.slug}`} >
                                        <EmailIcon size={50} />
                                    </EmailShareButton>

                                    <button className={"shareClose"} onClick={(e) => handleShareClick(e)} onKeyDown={(e) => handleShareClick(e)} onBlur={(e) => handleShareClick(e)}>
                                        <i className='material-icons'>close</i>
                                    </button>
                                </div>
                            </div>
                          }
                        </Flex>
                      </>
                    }
                  </LinkOverlay>
                </LinkBox>
              </SwiperSlide>
            )
          })}
        </Swiper>
      }
    </Box>
  );
}

export default SliderLandscapeFestival;